import {
    AnimalGender,
    AppointmentFieldsFragment,
    ClientFieldsFragment,
    ClinicFieldsFragment,
    ClinicianFieldsFragment,
    ClinicSettingsFieldsFragment,
    PatientFieldsFragment,
} from '~graphql/generated/graphql'
import { formatDistanceStrict } from 'date-fns'
import { getAddressString } from './helpers'
import { formatInTimeZone } from 'date-fns-tz'
import { formatPhoneReadable } from './phone'
import { getCustomMergeTagMap } from './custom-merge-tags'
import { hideEmptySocialIcons } from './hide-empty-social-icons'

export const APPOINTMENT_TEMPLATE_VARIABLE_KEYS = [
    'appointment_id',
    'appointment_time',
    'appointment_date',
    'appointment_service',
    'appointment_drop_off_time',
    'appointment_drop_off_date',
    'clinician_name',
    'client_previous_doctor',
    'patient_previous_doctor',
] as const

const APPOINTMENT_SELECTION_TEMPLATE_VARIABLE_KEYS = [
    'appointment_id',
    'appointment_time',
    'appointment_date',
    'appointment_service',
    'appointment_drop_off_time',
    'appointment_drop_off_date',
    'clinician_name',
    'google_calendar_event',
] as const

export type AppointmentTemplateVariableKey =
    (typeof APPOINTMENT_TEMPLATE_VARIABLE_KEYS)[number]

function hasTemplateVariable(
    body: string | undefined | null,
    key: string,
): boolean {
    return Boolean(body && new RegExp(`{{\\s*${key}\\s*}}`).test(body))
}

export function hasAppointmentTemplateVariables(
    body: string | undefined | null,
): boolean {
    if (!body) return false

    return APPOINTMENT_TEMPLATE_VARIABLE_KEYS.some(key =>
        hasTemplateVariable(body, key),
    )
}

export function hasAppointmentSelectionTemplateVariables(
    body: string | undefined | null,
): boolean {
    if (!body) return false

    return APPOINTMENT_SELECTION_TEMPLATE_VARIABLE_KEYS.some(key =>
        hasTemplateVariable(body, key),
    )
}

export function hasClientPreviousDoctorTemplateVariable(
    body: string | undefined | null,
): boolean {
    return hasTemplateVariable(body, 'client_previous_doctor')
}

export function hasPatientPreviousDoctorTemplateVariable(
    body: string | undefined | null,
): boolean {
    return hasTemplateVariable(body, 'patient_previous_doctor')
}

export function replaceTemplateVariables(
    body: string | undefined | null,
    templateMapper: Record<string, string> | undefined | null,
): { value: string } {
    if (!body || !templateMapper) {
        return { value: body || '' }
    }
    // Replace all template variables with their values
    const res = Object.keys(templateMapper).reduce((prev, key) => {
        const keyExp = new RegExp(`{{\\s*${key}\\s*}}`, 'g')
        if (keyExp.test(prev)) {
            if (
                key === 'repeated_message' ||
                key === 'reminder_list' ||
                key === 'due_patient_reminders'
            ) {
                // Wrap in pre tags so the per-line breaks render in HTML
                return prev?.replaceAll(
                    keyExp,
                    `<div style="white-space: pre-wrap;">${templateMapper[key]}</div>`,
                )
            }
            return prev?.replaceAll(
                keyExp,
                templateMapper[key] || `{{ ${key} }}`,
            )
        } else {
            return prev
        }
    }, body)

    return {
        value: hideEmptySocialIcons(res),
    }
}

export function getTemplateMapper({
    client,
    clinic,
    patient,
    appointment,
    clientPreviousDoctor,
    patientPreviousDoctor,
    googleCalendarEvent,
}: {
    client?: Pick<
        ClientFieldsFragment,
        'first_name' | 'last_name' | 'phone' | 'email' | 'address'
    > & {
        last_appointment?: {
            clinician?:
                | Pick<ClinicianFieldsFragment, 'name' | 'display_name'>
                | null
                | undefined
        } | null
    }
    clinic?: Pick<
        ClinicFieldsFragment,
        | 'name'
        | 'slug'
        | 'phone'
        | 'email'
        | 'address'
        | 'avatar_url'
        | 'website'
        | 'time_zone'
    > & {
        settings?:
            | Pick<
                  ClinicSettingsFieldsFragment,
                  'primary_color' | 'accent_color' | 'custom_merge_tags'
              >
            | null
            | undefined
    }
    patient?: Pick<PatientFieldsFragment, 'name' | 'gender' | 'birthday'> & {
        last_appointment?: {
            clinician?:
                | Pick<ClinicianFieldsFragment, 'name' | 'display_name'>
                | null
                | undefined
        } | null
    }
    appointment?: Pick<
        AppointmentFieldsFragment,
        'start_time' | 'drop_off_time' | 'service'
    > & {
        id?: string | null | undefined
        clinician?:
            | Pick<ClinicianFieldsFragment, 'name' | 'display_name'>
            | null
            | undefined
    }
    clientPreviousDoctor?: string | null | undefined
    patientPreviousDoctor?: string | null | undefined
    googleCalendarEvent?: string | null | undefined
}) {
    const timeZone = clinic?.time_zone || 'America/New_York'

    const pronouns = patient ? getPatientPronouns(patient.gender) : undefined
    const clientName = [client?.first_name, client?.last_name]
        .filter(value => Boolean(value?.trim()))
        .join(' ')
        .trim()
    const clientCountry = client?.address?.country || clinic?.address?.country
    const clientPhone = client?.phone
        ? formatPhoneReadable(client.phone, clientCountry) || client.phone
        : ''

    // Clinic-defined merge tags (e.g. {{ facebook_url }}). Spread first so any
    // collision with a built-in tag below resolves in favor of the built-in.
    const customMergeTagMap = getCustomMergeTagMap(
        clinic?.settings?.custom_merge_tags,
    )
    const templateMapper: Record<string, string> = {
        ...customMergeTagMap,
        first_name: client?.first_name?.trim() || '',
        last_name: client?.last_name?.trim() || '',
        client_name: clientName,
        client_email: client?.email?.trim() || '',
        client_phone: clientPhone,
        client_address: getAddressString(client?.address),
        client_previous_doctor: clientPreviousDoctor || '',
        client_street: [client?.address?.street_a, client?.address?.street_b]
            .filter(Boolean)
            .join(', '),
        client_city: client?.address?.city || '',
        client_state: client?.address?.state || '',
        client_zip: client?.address?.zip || '',
        patient_name: patient?.name?.trim() || '',
        patient_previous_doctor: patientPreviousDoctor || '',
        patient_sex:
            patient?.gender && patient.gender !== AnimalGender.Unknown
                ? patient.gender
                : '',
        patient_age: patient?.birthday
            ? formatDistanceStrict(new Date(patient.birthday), new Date())
            : '',
        date_today: formatInTimeZone(Date.now(), timeZone, 'PPPP'),
        he_or_she: pronouns?.he_or_she || '',
        him_or_her: pronouns?.him_or_her || '',
        his_or_her: pronouns?.his_or_her || '',
        appointment_id: appointment?.id || '',
        clinic_name: clinic?.name || '',
        clinic_slug: clinic?.slug || '',
        clinic_phone:
            formatPhoneReadable(clinic?.phone, clinic?.address?.country) ||
            clinic?.phone ||
            '',
        clinic_email: clinic?.email || '',
        clinic_address: getAddressString(clinic?.address),
        clinic_logo: clinic?.avatar_url || '',
        clinic_website: clinic?.website || '',
        clinic_color: clinic?.settings?.primary_color || '',
        primary_color: clinic?.settings?.primary_color || '',
        accent_color: clinic?.settings?.accent_color || '',
        appointment_time: appointment?.start_time
            ? formatInTimeZone(appointment?.start_time, timeZone, 'h:mm a')
            : '{{ appointment_time }}',
        appointment_service: appointment?.service?.name || '',
        appointment_date: appointment?.start_time
            ? formatInTimeZone(appointment?.start_time, timeZone, 'PPPP')
            : '{{ appointment_date }}',
        clinician_name:
            appointment?.clinician?.display_name ||
            appointment?.clinician?.name ||
            '',
        appointment_drop_off_time: appointment?.drop_off_time
            ? formatInTimeZone(appointment?.drop_off_time, timeZone, 'h:mm a')
            : '{{ appointment_drop_off_time }}',
        appointment_drop_off_date: appointment?.drop_off_time
            ? formatInTimeZone(appointment?.drop_off_time, timeZone, 'PPPP')
            : '{{ appointment_drop_off_date }}',
        google_calendar_event: googleCalendarEvent || '',
        // product_name:
        //     prescription?.product?.name ||
        //     patient_history_item?.product?.name ||
        //     '',
        // reminder_description:
        //     pims_reminder?.reminder_type?.display_name ||
        //     pims_reminder?.reminder_desc ||
        //     '',
        // reminder_due_date:
        //     pims_reminder?.display_due_date || pims_reminder?.send_date
        //         ? formatInTimeZone(
        //               custSegResult.pims_reminder.display_due_date ||
        //                   custSegResult.pims_reminder.send_date,
        //               timeZone,
        //               'PPPP',
        //           )
        //         : 'unknown date',
    }

    return templateMapper
}

type PatientPronouns = {
    /** Subject pronoun — `he` / `she` / `they`. */
    he_or_she: string
    /** Object pronoun — `him` / `her` / `them`. */
    him_or_her: string
    /** Possessive pronoun — `his` / `her` / `their`. */
    his_or_her: string
}

/**
 * Resolves the pronouns for a patient based on their `gender`. Falls back to
 * gender-neutral `they`/`them`/`their` when the gender is `Unknown` or missing
 * so the substituted message still reads naturally.
 *
 * Gender comes from GraphQL as a nullable string rather than the
 * `AnimalGender` enum, so we compare against the enum values directly.
 *
 * Keep this as the single source of truth for pronoun substitution in
 * client-facing templates.
 */
function getPatientPronouns(
    gender: string | null | undefined,
): PatientPronouns {
    if (gender === AnimalGender.Male) {
        return { he_or_she: 'he', him_or_her: 'him', his_or_her: 'his' }
    }
    if (gender === AnimalGender.Female) {
        return { he_or_she: 'she', him_or_her: 'her', his_or_her: 'her' }
    }
    return { he_or_she: 'they', him_or_her: 'them', his_or_her: 'their' }
}
